import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, MessageCircle, User } from 'lucide-react';

// Mock chatbot data
const chatbotData = {
  'office-policies': {
    title: 'Office Policies Assistant',
    questions: [
      {
        id: 'op1',
        question: 'What are the office working hours?',
        answer: 'The standard office hours are Monday to Saturday, 8:30 AM to 5:30 PM, with a 1-hour break (45 minutes for lunch and 15 minutes for prayer). Saturdays are non-working days except during VAT periods or project deadlines, compensating for national holidays that require work.',
        category: 'Working Hours'
      },
      {
        id: 'op2',
        question: 'What is the dress code policy?',
        answer: 'Employees must maintain a professional and classic appearance with light-colored, respectful clothing. Traditional attire (e.g., jalaba, abaya), shorts, jeans, excessive accessories, and heavy makeup are not permitted. Hair must be well-groomed, and personal hygiene must be impeccable.',
        category: 'Dress Code'
      },
      {
        id: 'op3',
        question: 'How do I request time off?',
        answer: 'For vacation leave, submit requests via email at least 3 weeks in advance after 6 months of continuous employment; you are entitled to 18 days of paid vacation per year. For personal commitments, request at least 1 week in advance. For emergency leave, notify your supervisor immediately. All absences, including tardiness, must be reported to HR as soon as possible. A medical certificate is required for sick leave exceeding 3 consecutive days, or it will be deducted from vacation days or salary.',
        category: 'Leave Policy'
      },
      {
        id: 'op4',
        question: 'Are there specific examples of attire not allowed in the office?',
        answer: 'Traditional attire such as jalaba or abaya, shorts, jeans, excessive accessories, and heavy makeup are explicitly prohibited to maintain a professional appearance.',
        category: 'Dress Code'
      },
      {
        id: 'op5',
        question: 'What are the expectations for personal grooming in the dress code?',
        answer: 'Employees must ensure their hair is well-groomed and maintain impeccable personal hygiene to uphold a professional and respectful appearance in the workplace.',
        category: 'Dress Code'
      },
      {
        id: 'op6',
        question: 'What are the procedures for check-in and check-out?',
        answer: 'Employees must use the fingerprint system to check in before 8:30 AM and check out after 5:30 PM. Check out before and check in after the 1-hour break (45 minutes for lunch, 15 minutes for prayer). Missed check-ins or check-outs must be reported to HR immediately. Late arrivals or unauthorized early departures may lead to penalties.',
        category: 'Check-in Procedures'
      },
      {
        id: 'op7',
        question: 'How should breaks be managed?',
        answer: 'Employees are entitled to a 1-hour break daily, split into 45 minutes for lunch and 15 minutes for prayer, taken as one block or separately. Break schedules are assigned by group: Group 1 (1:00 PM–1:45 PM) and Group 2 (1:45 PM–2:30 PM). Employees must check out before and check in after breaks, ensuring the total break time does not exceed 1 hour.',
        category: 'Break Policy'
      },
      {
        id: 'op8',
        question: 'What are the rules for using technology in the office?',
        answer: 'Use only company-approved software and hardware, keep software updated, and report security breaches immediately. Company computers and internet are for work-related purposes only; personal use must be minimized. Avoid visiting inappropriate sites, downloading illegal content, or using non-work-related streaming services during office hours.',
        category: 'Technology Use'
      },
      {
        id: 'op9',
        question: 'How should I handle task delegation before taking leave?',
        answer: 'Before taking time off, delegate your tasks and deadlines to a colleague to ensure continuity. Safeguard all company and customer information from unauthorized disclosure.',
        category: 'Task Delegation'
      },
      {
        id: 'op10',
        question: 'What is the policy on phone usage during work hours?',
        answer: 'Personal phone use for social media or other non-work purposes is prohibited during work hours. Phones must be stored in a bag or locker, not visible on desks. WhatsApp may be used via browser for work-related purposes only. Management and supervisors may use phones for work-related tasks.',
        category: 'Phone Usage'
      },
      {
        id: 'op11',
        question: 'What is the company’s stance on discrimination and harassment?',
        answer: 'The company has a zero-tolerance policy for discrimination or harassment based on race, gender, national origin, age, disability, or other protected characteristics. Report incidents to your supervisor or HR for thorough investigation and potential disciplinary action.',
        category: 'Anti-Discrimination'
      },
      {
        id: 'op12',
        question: 'How should I report issues or communicate with my team?',
        answer: 'Report any productivity issues to management promptly. Communicate respectfully and professionally, respond to emails within 24 hours during business days, and use appropriate language in all written communications. Support and collaborate with team members as needed.',
        category: 'Communication'
      },
      {
        id: 'op13',
        question: 'What are the expectations for client interactions and company representation?',
        answer: 'Interact with clients courteously and professionally to ensure high satisfaction. Represent the company professionally both inside and outside the workplace. Manage time effectively, prioritize tasks, and meet deadlines.',
        category: 'Client Interaction'
      },
      {
        id: 'op14',
        question: 'What are the health and safety guidelines in the office?',
        answer: 'Adhere to health and safety guidelines and report any hazards to management immediately to ensure a safe working environment.',
        category: 'Health and Safety'
      },
      {
        id: 'op15',
        question: 'How should I handle conflicts in the workplace?',
        answer: 'Address conflicts professionally and promptly. Provide constructive feedback and suggestions to improve work processes, and collaborate with your team to resolve issues.',
        category: 'Conflict Resolution'
      },
      {
        id: 'op16',
        question: 'What are the rules for completing tasks before leaving the office?',
        answer: 'Ensure all assigned tasks are completed before leaving the office for the day. Report any difficulties affecting productivity to the management team promptly.',
        category: 'Task Completion'
      },
      {
        id: 'op17',
        question: 'What are the consequences of failing to follow leave policies?',
        answer: 'Failure to comply with leave policies, such as not obtaining prior approval for non-emergency leave or not reporting absences, may result in the leave being considered unauthorized and potential deductions from vacation days or salary.',
        category: 'Leave Policy'
      },
      {
        id: 'op18',
        question: 'How is data protection handled in the office?',
        answer: 'Regularly back up important data, encrypt sensitive information, and follow company policies for data retention and disposal to ensure data protection.',
        category: 'Data Protection'
      },
      {
        id: 'op19',
        question: 'What is the process for employee warnings and corrective actions?',
        answer: 'For policy violations or tardiness, employees receive up to three documented verbal warnings, followed by up to two written warnings via email, which require acknowledgment. If no improvement occurs, a formal meeting with the manager is scheduled to discuss further actions.',
        category: 'Corrective Actions'
      },
      {
        id: 'op20',
        question: 'When can new employees start, and what is required before their start date?',
        answer: 'New employees must start on the first or third Monday of the month. If these days are not Mondays, the start date shifts to the following Monday. All required documentation must be completed before starting.',
        category: 'Onboarding'
      },
      {
        id: 'op21',
        question: 'How are managers informed about new employee onboarding?',
        answer: 'HR sends the onboarding agenda, including candidate details, to the Managers of Nador and Tangier, Marabes Academy, IT Department, and Planning Team at least one week before the new employee’s start date.',
        category: 'Onboarding'
      },
      {
        id: 'op22',
        question: 'What are the steps for resigning from the company?',
        answer: 'Submit a resignation letter or email to the Managers of Nador and Tangier, with HR in CC, at least one month before the last working day. Work with your manager to delegate tasks, complete a handover, finalize clearance, and return company property. HR conducts an exit interview, and final documents (e.g., Certificate of Employment) are provided.',
        category: 'Offboarding'
      },
      {
        id: 'op23',
        question: 'How is the offboarding process communicated and recorded?',
        answer: 'HR informs relevant departments (IT, Planning, Marabes Academy) via email and the HR WhatsApp group. Departure details, including notice period, last working day, handover status, and clearance, are recorded in a shared Excel spreadsheet accessible to management.',
        category: 'Offboarding'
      },
      {
        id: 'op24',
        question: 'What are the specific steps for submitting a vacation request?',
        answer: 'Complete the Vacation Request Form, including start/end dates, total days, vacation period, and backup colleague’s name. Email the form to your Managers, Account Manager, Project Manager, and Quality Reviewer, with HR in CC at hr@marabes.nl, at least 3 weeks in advance. Brief the backup colleague and share necessary documents before the vacation. HR will confirm approval or denial via email.',
        category: 'Leave Policy'
      },
      {
        id: 'op25',
        question: 'What happens if I submit an incomplete or late vacation request?',
        answer: 'Incomplete or late vacation requests may be rejected by HR to ensure proper planning and team coordination.',
        category: 'Leave Policy'
      },
      {
        id: 'op26',
        question: 'Who needs to approve my vacation request?',
        answer: 'Your vacation request requires approval from your Account Manager, Project Manager, and Quality Reviewer. HR verifies the process and provides official confirmation of approval or denial.',
        category: 'Leave Policy'
      },
      {
        id: 'op27',
        question: 'What documentation is provided upon leaving the company?',
        answer: 'Upon departure, employees receive a Certificate of Internship or Certificate of Employment, signed by their direct manager, and their final payslip.',
        category: 'Offboarding'
      },
      {
        id: 'op28',
        question: 'What is the purpose of the exit interview during offboarding?',
        answer: 'The exit interview, conducted by HR, gathers feedback from the departing employee to improve processes and address any concerns, ensuring a smooth transition.',
        category: 'Offboarding'
      },
      {
        id: 'op29',
        question: 'What are the consequences of repeated policy violations after warnings?',
        answer: 'After three verbal warnings and two written warnings, continued policy violations lead to a formal meeting with the manager to discuss further actions, which may include more serious disciplinary measures.',
        category: 'Corrective Actions'
      },
      {
        id: 'op30',
        question: 'How is the handover process managed during offboarding?',
        answer: 'Before resigning, employees work with their manager to delegate tasks and prepare a detailed handover document. The manager ensures tasks are assigned and updates team points of contact to maintain workflow continuity.',
        category: 'Offboarding'
      },
      {
        id: 'op31',
        question: 'What departments are involved in the onboarding process?',
        answer: 'The onboarding process involves the Managers of Nador and Tangier, Marabes Academy, IT Department, and Planning Team, who are notified by HR at least one week in advance with candidate details.',
        category: 'Onboarding'
      },
      {
        id: 'op32',
        question: 'What should I include in the Vacation Request Form?',
        answer: 'The Vacation Request Form must include your name, department, position, start and end dates, total number of vacation days, vacation period (e.g., Week 32 / August 5–9), and the name of your backup colleague.',
        category: 'Leave Policy'
      },
      {
        id: 'op33',
        question: 'How is personal commitment leave handled?',
        answer: 'Request personal commitment leave via email at least 48 hours in advance. In urgent cases, inform HR by any means. Absences exceeding 2 hours are counted as a full day of leave, with additional cases reviewed on a case-by-case basis.',
        category: 'Leave Policy'
      },
      {
        id: 'op34',
        question: 'How often are employee evaluations conducted?',
        answer: 'Existing employees are evaluated annually in December. New employees are evaluated four times in their first year: after 1, 3, 6, and 12 months.',
        category: 'Employee Evaluation'
      },
      {
        id: 'op35',
        question: 'What criteria are used to evaluate employees?',
        answer: 'Evaluations assess attendance and punctuality, work performance (quality, productivity, deadlines), behavior and attitude, teamwork and communication, responsibility and initiative, skills and development, and optional self-evaluation.',
        category: 'Employee Evaluation'
      },
      {
        id: 'op36',
        question: 'What are the steps in the employee evaluation process?',
        answer: 'HR provides the evaluation form and schedule. The supervisor completes the form, discusses it with HR and management, meets with the employee to review feedback, creates an action plan with goals and timelines, and HR stores the documentation for follow-up.',
        category: 'Employee Evaluation'
      },
      {
        id: 'op37',
        question: 'What platforms are used to recruit candidates?',
        answer: 'Candidates are sourced through Instagram, LinkedIn, Facebook Ads, Indeed, and referrals from current employees.',
        category: 'Recruitment'
      },
      {
        id: 'op38',
        question: 'What happens during the initial phone screening for candidates?',
        answer: 'The initial phone screening confirms the candidate’s availability, interest, personality, and key details like experience, current situation, and expectations.',
        category: 'Recruitment'
      },
      {
        id: 'op39',
        question: 'What is assessed during the first interview for a job candidate?',
        answer: 'The first interview, conducted via WhatsApp video call, assesses the candidate’s knowledge of MARABES GROUP, reasons for interest, personal details (age, marital status, education, experience, location, future plans, salary expectations), communication skills, professionalism, confidence, and cultural fit.',
        category: 'Recruitment'
      }
    ]
  },
  'hr-handbook': {
    title: 'HR Handbook Assistant - MARABES GROUP',
    questions: [
      {
        id: 'hr1',
        question: 'What is the employee onboarding process?',
        answer: 'New employees must start on the 1st or 3rd Monday of each month. The process includes: HR notification to all departments one week prior, completion of required documentation, IT setup (laptop and system access), company protocol explanation, team introductions, office tour, and coordination with Marabes Academy for training. All candidates must complete documentation before starting.',
        category: 'Onboarding'
      },
      {
        id: 'hr2',
        question: 'What documents are required for new employees?',
        answer: 'Required documents include: ID, education diplomas, training certificates, criminal certificate, and ANAPEC certificate. All documents must be completed before the employee can start work.',
        category: 'Onboarding'
      },
      {
        id: 'hr3',
        question: 'What types of employment contracts does MARABES offer?',
        answer: 'MARABES offers several contract types: 1) Pre-employment internship (1 month evaluation), 2) CDD Contract (1-year fixed-term with full salary and CNSS), 3) ANAPEC Contract (2-year training contract), 4) PCS Contract (up to 1 year after ANAPEC completion), 5) CDI Contract (permanent employment without fixed end date).',
        category: 'Employment Contracts'
      },
      {
        id: 'hr4',
        question: 'What is the Marabes Academy training program?',
        answer: 'The Marabes Academy is a 4-week comprehensive training program: Week 1 - Company introduction, Basecone software, Exact software, bank statements, and financial reports. Week 2 - Working with Account Manager, task evaluation, monthly closing procedures. Week 3 - VAT declarations, comprehensive recap, practice with AMs. Week 4 - Q&A session, ICP declarations, final evaluation and certification.',
        category: 'Training'
      },
      {
        id: 'hr5',
        question: 'What happens if someone fails the Marabes Academy evaluation?',
        answer: 'If a candidate does not pass the evaluation test, they will receive financial compensation in the form of reimbursement for transportation costs. This acknowledges their effort and ensures fair treatment for their time and travel investment.',
        category: 'Training'
      },
      {
        id: 'hr6',
        question: 'How do performance evaluations work at MARABES?',
        answer: 'Existing employees are evaluated annually in December (January 1st to December 31st period). New employees have four evaluations during their first year: after 1, 3, 6, and 12 months. Evaluations cover attendance & punctuality, work performance, behavior & attitude, teamwork & communication, responsibility & initiative, and skills & development.',
        category: 'Performance Management'
      },
      {
        id: 'hr7',
        question: 'Who is involved in the evaluation process?',
        answer: 'The evaluation process involves the direct supervisor, HR department, management team, and the employee. The supervisor completes the evaluation form, discusses it with HR and management, then meets with the employee to create an action plan with clear goals and timelines.',
        category: 'Performance Management'
      },
      {
        id: 'hr8',
        question: 'What are the working hours and break policies?',
        answer: 'Employees must be at their desks by 8:30 AM for an 8-hour workday. Standard work is Monday through Saturday (44 hours/week), with Saturdays as compensation for national holidays (except during VAT periods). Break time is 1 hour total: 45 minutes for lunch/coffee and 15 minutes personal. Break groups: Group 1 (13:00-13:45), Group 2 (13:45-14:30).',
        category: 'Work Policies'
      },
      {
        id: 'hr9',
        question: 'What is the dress code policy?',
        answer: 'Maintain professional and classic appearance. Traditional attire (Jellaba, Abaya, etc.) is not permitted. Choose light-colored, respectful clothing with minimal distractions. Hair must be well-groomed with impeccable personal hygiene. Avoid shorts, jeans, excessive accessories, and heavy makeup.',
        category: 'Work Policies'
      },
      {
        id: 'hr10',
        question: 'What is the phone usage policy?',
        answer: 'Personal phone use during work hours for social media and personal purposes is prohibited. Phones must not be visible on desks and should be stored in bags or lockers. WhatsApp may only be used via browser for work-related purposes. Management and supervisors may use phones for work-related purposes. Project Managers ensure team compliance.',
        category: 'Work Policies'
      },
      {
        id: 'hr11',
        question: 'How do I request vacation time?',
        answer: 'Submit vacation requests at least 3 weeks in advance using the "Vacation Request Form - MARABES." Email the completed form to your managers, Account Manager, Project Manager, Quality Reviewer, and CC hr@marabes.nl. Include vacation period, total days, and backup colleague name. Employees get 18 paid vacation days annually. No two team members can be on vacation simultaneously.',
        category: 'Leave & Vacation'
      },
      {
        id: 'hr12',
        question: 'What is the sick leave policy?',
        answer: 'Submit a medical certificate within 48 hours of absence. With a certificate, absence is treated as sick leave and days are deducted from salary. Without a certificate, absence counts as vacation time. If no vacation days remain, absence is deducted directly from salary.',
        category: 'Leave & Vacation'
      },
      {
        id: 'hr13',
        question: 'How do I request emergency or personal leave?',
        answer: 'For emergency leave, notify your supervisor immediately. Emergency leave may be paid or unpaid based on the situation. For personal commitment leave, request at least 48 hours in advance by email. In urgent situations, inform HR by any available means. Any absence exceeding 2 hours counts as a full day.',
        category: 'Leave & Vacation'
      },
      {
        id: 'hr14',
        question: 'What is the recruitment and selection process?',
        answer: 'The process has 5 steps: 1) Finding candidates via Instagram, LinkedIn, Facebook Ads, Indeed, and employee referrals. 2) Screening applications and initial phone call. 3) First interview via WhatsApp video call covering company knowledge, general and specific questions. 4) Second in-person interview with manager and team lead. 5) Job offer via WhatsApp and call to discuss contract details.',
        category: 'Recruitment'
      },
      {
        id: 'hr15',
        question: 'What is the employee resignation process?',
        answer: 'Employees must provide at least one month written notice via email to Nador and Tangier managers with HR in CC. HR informs all relevant departments. Tasks are delegated to colleagues with detailed handover. Employee completes clearance process, submits final resignation, and receives Certificate of Employment/Internship and final payslip.',
        category: 'Offboarding'
      },
      {
        id: 'hr16',
        question: 'What are MARABES core values?',
        answer: 'MARABES core values include: Integrity (acting honestly and transparently), Professionalism (professional behavior in all communications), Customer focus (prioritizing customer satisfaction), and Collaboration (working together, sharing knowledge, and supporting each other in achieving goals).',
        category: 'Company Values'
      },
      {
        id: 'hr17',
        question: 'What does the Code of Conduct cover?',
        answer: 'The Code of Conduct requires employees to: protect confidential customer data, avoid conflicts of interest and report them to HR, refrain from discrimination/harassment/unprofessional behavior, and always act honestly and transparently regarding business activities and customer relationships. Violations may result in disciplinary action including dismissal.',
        category: 'Company Values'
      },
      {
        id: 'hr18',
        question: 'How does communication work at MARABES?',
        answer: 'MARABES uses both top-down and bottom-up communication. Top-down: Management meetings → Department heads → Team leaders → Employees, with HR supporting communication of policies and updates. Bottom-up: Employees can share feedback through HR as accessible department, regular feedback sessions, and monthly open office hours.',
        category: 'Communication'
      },
      {
        id: 'hr19',
        question: 'How does salary reporting work?',
        answer: 'HR prepares comprehensive monthly reports for each employee by the 26th or 27th of each month. Reports include total working hours, overtime, absences, and new employees. Salaries are processed and paid between the 28th and 30th. Special procedures apply for mid-month starters and departing employees.',
        category: 'Salary & Benefits'
      },
      {
        id: 'hr20',
        question: 'How are workplace conflicts resolved?',
        answer: 'HR manages conflicts through: 1) Listening to concerns in a safe space, 2) Fair investigation gathering information from all parties, 3) Mediation as neutral party, 4) Implementing clear solutions, 5) Follow-up to ensure resolution effectiveness. All conflicts are addressed quickly and fairly to maintain a respectful workplace.',
        category: 'Conflict Resolution'
      },
      {
        id: 'hr21',
        question: 'What are the guidelines for client interaction?',
        answer: 'Client interaction guidelines include: Clear communication to understand client needs, Active listening with clarifying questions, Professional and respectful demeanor, Efficient problem-solving with practical solutions, and Regular follow-up to ensure satisfaction and resolve concerns.',
        category: 'Client Relations'
      }
    ]
  }
};

const ChatbotModal = ({ isOpen, onClose, policyType = 'office-policies' }) => {
  const [messages, setMessages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInitialQuestions, setShowInitialQuestions] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showQuickQuestions, setShowQuickQuestions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const data = chatbotData[policyType];
  const categories = ['all', ...new Set(data?.questions.map(q => q.category) || [])];

  // Reset chat when modal opens or policyType changes
  useEffect(() => {
    if (isOpen) {
      resetChat();
    }
  }, [isOpen, policyType]);

  // Ensure questions update when selectedCategory changes
  useEffect(() => {
    const filteredQuestions = selectedCategory === 'all'
      ? data?.questions
      : data?.questions.filter(q => q.category === selectedCategory);
    console.log(`Selected Category: ${selectedCategory}, Filtered Questions:`, filteredQuestions);
    setShowInitialQuestions(true);
    setShowQuickQuestions(false);
  }, [selectedCategory, data]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, showQuickQuestions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resetChat = () => {
    setMessages([]);
    setInputValue('');
    setShowInitialQuestions(true);
    setShowQuickQuestions(false);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages([{
        type: 'bot',
        content: `👋 Hello! I'm your ${data?.title || 'Policy Assistant'}. I'm here to help you with any questions about our policies. You can select a category above, click on the questions below, or type your own question!`,
        timestamp: new Date()
      }]);
    }, 1500);
  };

  const typeMessage = (text, callback) => {
    let currentIndex = 0;
    const tempMessage = { type: 'bot', content: '', timestamp: new Date() };
    setMessages(prev => [...prev, tempMessage]);

    const typeInterval = setInterval(() => {
      if (currentIndex <= text.length) {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            ...tempMessage,
            content: text.substring(0, currentIndex)
          };
          return newMessages;
        });
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        if (callback) callback();
      }
    }, 30);
  };

  const showQuickQuestionsAfterAnswer = () => {
    setShowQuickQuestions(true);
  };

  const handleQuestionClick = (question) => {
    setMessages(prev => [
      ...prev,
      {
        type: 'user',
        content: question.question,
        timestamp: new Date()
      }
    ]);

    setShowInitialQuestions(false);
    setShowQuickQuestions(false);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      typeMessage(question.answer, () => {
        setTimeout(() => {
          showQuickQuestionsAfterAnswer();
        }, 800);
      });
    }, 800 + Math.random() * 1200);
  };

  const getRandomQuestions = (arr, num) => {
    return [...arr]
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.min(num, arr.length));
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setShowInitialQuestions(false);
    setShowQuickQuestions(false);

    const filteredQuestions = selectedCategory === 'all'
      ? data?.questions
      : data?.questions.filter(q => q.category === selectedCategory);

    const matchedQuestion = filteredQuestions.find(q =>
      q.question.toLowerCase().includes(inputValue.toLowerCase()) ||
      inputValue.toLowerCase().includes(q.question.toLowerCase().split(' ')[0])
    );

    setTimeout(() => {
      setIsTyping(false);
      const responseText = matchedQuestion
        ? matchedQuestion.answer
        : `I understand you're asking about that topic. While I don't have a specific answer ready in the ${selectedCategory === 'all' ? 'general policies' : selectedCategory} category, I'd recommend checking our policy documents or reaching out to HR for detailed information. Feel free to ask about our other topics or try a different category! 😊`;
      
      typeMessage(responseText, () => {
        setTimeout(() => {
          showQuickQuestionsAfterAnswer();
        }, 800);
      });
    }, 1000 + Math.random() * 800);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const TypingIndicator = () => (
    <div style={styles.message}>
      <div style={styles.botAvatar}>
        <Bot size={16} color="#2196f3" />
      </div>
      <div style={{ ...styles.messageContent, ...styles.botMessageContent, ...styles.typingIndicator }}>
        <div style={styles.typingDots}>
          <div style={styles.dot}></div>
          <div style={styles.dot}></div>
          <div style={styles.dot}></div>
        </div>
      </div>
    </div>
  );

  const QuickQuestions = ({ inChat = false }) => {
    const filteredQuestions = selectedCategory === 'all'
      ? data?.questions
      : data?.questions.filter(q => q.category === selectedCategory);

    if (!filteredQuestions || filteredQuestions.length === 0) {
      return (
        <div style={styles.noQuestions}>
          No questions available for this category.
        </div>
      );
    }

    if (inChat) {
      return (
        <div style={styles.inChatQuestions}>
          <div style={styles.inChatQuestionsHeader}>
            <MessageCircle size={14} />
            <span>Quick questions you might ask:</span>
          </div>
          <div style={styles.inChatQuestionsList}>
            {getRandomQuestions(filteredQuestions, 5).map((question) => (
              <button
                key={question.id}
                style={styles.inChatQuestionButton}
                onClick={() => handleQuestionClick(question)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f4ff';
                  e.target.style.borderColor = '#667eea';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                }}
                aria-label={`Ask about ${question.question}`}
              >
                {question.question}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div style={styles.questionsGrid}>
        <div style={styles.questionsTitle}>
          <MessageCircle size={16} />
          <span>Quick Questions</span>
        </div>
        <div style={styles.questionsList}>
          {filteredQuestions.map((question) => (
            <button
              key={question.id}
              style={styles.questionButton}
              onClick={() => handleQuestionClick(question)}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, styles.questionButtonHover);
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = '#e5e7eb';
              }}
              aria-label={`Ask about ${question.question}`}
            >
              {question.question}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease',
      backdropFilter: 'blur(4px)'
    },
    modal: {
      width: '90%',
      maxWidth: '900px',
      height: '85vh',
      backgroundColor: 'var(--card-bg, white)',
      borderRadius: '20px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
      animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      overflow: 'hidden'
    },
    header: {
      padding: '24px 28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: policyType === 'office-policies'
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      position: 'relative',
      overflow: 'hidden'
    },
    headerBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="7" cy="7" r="7"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#ffffff',
      fontSize: '1.3rem',
      fontWeight: '600',
      position: 'relative',
      zIndex: 1
    },
    botIcon: {
      background: 'rgba(255, 255, 255, 0.2)',
      padding: '8px',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)'
    },
    closeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '12px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      position: 'relative',
      zIndex: 1
    },
    resetButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '12px',
      padding: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      position: 'relative',
      zIndex: 1
    },
    chatContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px 24px',
      backgroundColor: '#f8fafc',
      backgroundImage: 'linear-gradient(45deg, #f8fafc 25%, transparent 25%), linear-gradient(-45deg, #f8fafc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f4f8 75%), linear-gradient(-45deg, transparent 75%, #f0f4f8 75%)',
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
    },
    message: {
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'flex-end',
      gap: '12px',
      animation: 'messageSlideIn 0.4s ease'
    },
    userMessage: {
      flexDirection: 'row-reverse'
    },
    botMessage: {
      flexDirection: 'row'
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    botAvatar: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff'
    },
    userAvatar: {
      background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      color: '#8b4513'
    },
    messageContent: {
      maxWidth: '70%',
      padding: '14px 18px',
      fontSize: '0.95rem',
      lineHeight: '1.5',
      position: 'relative',
      wordBreak: 'break-word'
    },
    botMessageContent: {
      backgroundColor: 'var(--card-bg, white)',
      color: '#374151',
      borderRadius: '18px 18px 18px 4px',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.05)'
    },
    userMessageContent: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff',
      borderRadius: '18px 18px 4px 18px',
      boxShadow: '0 2px 12px rgba(102, 126, 234, 0.25)'
    },
    typingIndicator: {
      padding: '16px 20px'
    },
    typingDots: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    },
    dot: {
      width: '8px',
      height: '8px',
      backgroundColor: '#94a3b8',
      borderRadius: '50%',
      animation: 'typingBounce 1.4s infinite'
    },
    inChatQuestions: {
      marginBottom: '20px',
      animation: 'slideInFromBottom 0.5s ease',
      pointerEvents: 'auto',
      position: 'relative',
      zIndex: 10
    },
    inChatQuestionsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#6b7280',
      fontSize: '0.85rem',
      fontWeight: '500',
      marginBottom: '12px',
      paddingLeft: '48px'
    },
    inChatQuestionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      paddingLeft: '48px'
    },
    inChatQuestionButton: {
      padding: '12px 16px',
      backgroundColor: 'var(--card-bg, white)',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '0.9rem',
      color: '#374151',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      width: '100%',
      outline: 'none',
      userSelect: 'none',
      WebkitTapHighlightColor: 'transparent'
    },
    inputArea: {
      padding: '20px 24px',
      backgroundColor: 'var(--card-bg, white)',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-end'
    },
    inputContainer: {
      flex: 1,
      position: 'relative'
    },
    input: {
      width: '100%',
      padding: '14px 18px',
      borderRadius: '25px',
      border: '2px solid #e5e7eb',
      fontSize: '0.95rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit',
      resize: 'none',
      minHeight: '48px',
      maxHeight: '120px'
    },
    inputFocused: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
    },
    sendButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      width: '48px',
      height: '48px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
    },
    sendButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    categoryFilter: {
      padding: '16px 24px',
      backgroundColor: 'var(--card-bg, white)',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },
    categoryButton: {
      padding: '8px 16px',
      borderRadius: '20px',
      border: '2px solid #e5e7eb',
      backgroundColor: 'var(--card-bg, white)',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      color: '#374151'
    },
    categoryButtonActive: {
      borderColor: '#667eea',
      backgroundColor: '#667eea',
      color: '#ffffff',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
    },
    questionsGrid: {
      padding: '16px 24px',
      backgroundColor: 'var(--card-bg, white)',
      maxHeight: '200px',
      overflowY: 'auto',
      borderTop: '1px solid #e5e7eb'
    },
    questionsTitle: {
      fontSize: '0.9rem',
      color: '#6b7280',
      marginBottom: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    questionsList: {
      display: 'grid',
      gap: '8px'
    },
    questionButton: {
      padding: '12px 16px',
      backgroundColor: '#f8fafc',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '0.9rem',
      color: '#374151',
      position: 'relative',
      overflow: 'hidden'
    },
    questionButtonHover: {
      backgroundColor: '#e5e7eb',
      transform: 'translateX(4px)',
      borderColor: '#667eea'
    },
    noQuestions: {
      padding: '16px',
      color: '#6b7280',
      fontSize: '0.9rem',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.headerBg}></div>
        
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <div style={styles.botIcon}>
              <Bot size={24} />
            </div>
            <span>{data?.title}</span>
          </div>
          <div>
            <button
              style={styles.resetButton}
              onClick={resetChat}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Reset conversation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
            </button>
            <button
              style={styles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Close chatbot"
            >
              <X size={20} color="#ffffff" />
            </button>
          </div>
        </div>

        <div style={styles.chatContainer}>
          {/* Category Filter - Always visible */}
          <div style={styles.categoryFilter}>
            <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500' }}>
              Filter by Category:
            </span>
            {categories.map(cat => (
              <button
                key={cat}
                style={{
                  ...styles.categoryButton,
                  ...(selectedCategory === cat ? styles.categoryButtonActive : {})
                }}
                onClick={() => {
                  setSelectedCategory(cat);
                  setMessages([{
                    type: 'bot',
                    content: `👋 Now showing questions for the ${cat === 'all' ? 'all policies' : cat} category. Select a question below or type your own!`,
                    timestamp: new Date()
                  }]);
                  setShowInitialQuestions(true);
                  setShowQuickQuestions(false);
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== cat) {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.backgroundColor = '#f0f4ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== cat) {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }
                }}
                aria-label={`Filter by ${cat} category`}
                role="option"
                aria-selected={selectedCategory === cat}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1).replace(/([A-Z])/g, ' $1')}
              </button>
            ))}
          </div>

          {/* Messages Area */}
          <div style={styles.messagesArea}>
            {messages.map((message, index) => (
              <div
                key={index}
                style={{
                  ...styles.message,
                  ...(message.type === 'bot' ? styles.botMessage : styles.userMessage)
                }}
              >
                <div style={{
                  ...styles.avatar,
                  ...(message.type === 'bot' ? styles.botAvatar : styles.userAvatar)
                }}>
                  {message.type === 'bot' ? <Bot size={16} /> : <User size={16} />}
                </div>
                <div style={{
                  ...styles.messageContent,
                  ...(message.type === 'bot' ? styles.botMessageContent : styles.userMessageContent)
                }}>
                  {message.content}
                </div>
              </div>
            ))}
            
            {isTyping && <TypingIndicator />}
            
            {showQuickQuestions && <QuickQuestions inChat={true} />}
            
            <div ref={messagesEndRef} />
          </div>

          {showInitialQuestions && <QuickQuestions />}

          {/* Input Area */}
          <div style={styles.inputArea}>
            <div style={styles.inputContainer}>
              <textarea
                ref={inputRef}
                style={{
                  ...styles.input,
                  ...(inputRef.current === document.activeElement ? styles.inputFocused : {})
                }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question here..."
                disabled={isTyping}
                aria-label="Type your question"
              />
            </div>
            <button
              style={{
                ...styles.sendButton,
                ...((!inputValue.trim() || isTyping) ? styles.sendButtonDisabled : {})
              }}
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
              aria-label="Send question"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes typingBounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }
        .dot:nth-child(1) { animation-delay: 0s; }
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

export default ChatbotModal;